// @flow
import update from 'immutability-helper';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import {
  deleteAddressApi, getAddressesApi,
  postAddressApi, putAddressApi,
  endpoints as addressEndpoints,
} from '../preferences/saved-addresses/api';
import { postOrderApi, uploadImageApi, uploadImageURLApi, getLastContactInfoApi } from './api';
import { push } from 'react-router-redux';
import { dataURItoBlob } from '../../utils/file';
import { setNewAlert } from '../alert/duck';
import { actions as SearchStepActions, initialState as SearchStepInitialState } from './SearchStep/duck';
import { initialState as ScrapStepInitialState } from './ScrapStep/duck';
import { actions as ProductStepActions, initialState as ProductStepInitialState } from './ProductStep/duck';
import { actions as QuoteStepActions, initialState as QuoteStepInitialState } from './QuoteStep/duck';
import { actions as CheckoutStepActions, initialState as CheckoutStepInitialState } from './CheckoutStep/duck';
import {
  actions as CheckoutSuccessStepActions,
  initialState as CheckoutSuccessStepInitialState,
} from './CheckoutSuccessStep/duck';

import SearchStepReducer from './SearchStep/duck';
import ProductStepReducer from './ProductStep/duck';
import QuoteStepReducer from './QuoteStep/duck';
import CheckoutStepReducer from './CheckoutStep/duck';
import CheckoutSuccessStepReducer from './CheckoutSuccessStep/duck';

import type { Address, Country, Order } from '../../entities';
import type { ScrappedProduct } from '../../entities/scrapped-product';
import type { HTTPError } from '../../error';
import type { CheckoutStepState } from './CheckoutStep/duck';
import type { CheckoutSuccessStepState } from './CheckoutSuccessStep/duck';
import type { PostOrderApiReq, PostOrderItemApiReq } from './api';
import type { RootState } from '../index';
import type { SearchStepState } from './SearchStep/duck';
import type { ScrapStepState } from './ScrapStep/duck';
import type { ProductStepState } from './ProductStep/duck';
import type { QuoteStepState } from './QuoteStep/duck';
import type { AddressRequest } from '../preferences/saved-addresses/api';

export type Step = 'scrap' | 'product' | 'quote' | 'checkout';
export type QuoteType = 'bag' | 'cart' | 'scrap';

export type ContactInfo = {
  +idNumber: string;
  +firstName: string;
  +lastName: string;
  +phone: string;
  +type: string;
};

type NewOrderAction = {
  +type: string;
  +initialQuery?: string;
  +createdOrder?: Order;
  +order?: Order;
  +requestOrder?: Order;
  +error?: HTTPError;
  +addressId?: string;
  +address?: Address;
  +addresses?: Address[];
  +requestCountry?: Country;
  +imageIds?: string[];
  +paymentMethods?: { [key: string]: string };
  +contactInfo?: ContactInfo;
  +country?: string;
};

export type NewOrderState = {
  +searchStep: SearchStepState;
  +scrapStep: ScrapStepState;
  +productStep: ProductStepState;
  +quoteStep: QuoteStepState;
  +checkoutStep: CheckoutStepState;
  +checkoutSuccessStep: CheckoutSuccessStepState;
  +initialQuery?: string;
  +preloadedProduct?: ScrappedProduct;
  +isPosting?: boolean;
  +createdOrder?: Order;
  +postError?: HTTPError;
  +isFetchingAddresses?: boolean;
  +isPuttingAddress?: boolean;
  +isDeletingAddress?: boolean;
  +addresses?: Address[];
  +closeAddressModal?: boolean;
  +successDelete?: boolean;
  +fetchAddressesError?: HTTPError;
  +editAddressError?: HTTPError;
  +deleteAddressError?: HTTPError;
  +isFetchingPaymentMethods?: boolean;
  +fetchPaymentMethodsError?: HTTPError;
  +paymentMethods?: { [key: string]: string };
  +requestOrder?: Order;
  +contactInfo?: ContactInfo;
  +getLastContactInfoError?: HTTPError;
};

// Actions

const NEW_ORDER_POST_ORDER_REQUEST = 'NEW_ORDER_POST_ORDER_REQUEST';
const NEW_ORDER_POST_ORDER_IMAGE_SUCCESS = 'NEW_ORDER_POST_ORDER_IMAGE_SUCCESS';
const NEW_ORDER_POST_ORDER_SUCCESS = 'NEW_ORDER_POST_ORDER_SUCCESS';
const NEW_ORDER_POST_ORDER_ERROR = 'NEW_ORDER_POST_ORDER_ERROR';

const NEW_ORDER_CHECKOUT_FETCH_ADDRESSES_REQUEST = 'NEW_ORDER_CHECKOUT_FETCH_ADDRESSES_REQUEST';
const NEW_ORDER_CHECKOUT_FETCH_ADDRESSES_SUCCESS = 'NEW_ORDER_CHECKOUT_FETCH_ADDRESSES_SUCCESS';
const NEW_ORDER_CHECKOUT_FETCH_ADDRESSES_ERROR = 'NEW_ORDER_CHECKOUT_FETCH_ADDRESSES_ERROR';

const NEW_ORDER_CHECKOUT_POST_ADDRESS_REQUEST = 'NEW_ORDER_CHECKOUT_POST_ADDRESS_REQUEST';
const NEW_ORDER_CHECKOUT_POST_ADDRESS_SUCCESS = 'NEW_ORDER_CHECKOUT_POST_ADDRESS_SUCCESS';
const NEW_ORDER_CHECKOUT_POST_ADDRESS_ERROR = 'NEW_ORDER_CHECKOUT_POST_ADDRESS_ERROR';

const NEW_ORDER_CHECKOUT_PUT_ADDRESS_REQUEST = 'NEW_ORDER_CHECKOUT_PUT_ADDRESS_REQUEST';
const NEW_ORDER_CHECKOUT_PUT_ADDRESS_SUCCESS = 'NEW_ORDER_CHECKOUT_PUT_ADDRESS_SUCCESS';
const NEW_ORDER_CHECKOUT_PUT_ADDRESS_ERROR = 'NEW_ORDER_CHECKOUT_PUT_ADDRESS_ERROR';

const NEW_ORDER_CHECKOUT_DELETE_ADDRESS_REQUEST = 'NEW_ORDER_CHECKOUT_DELETE_ADDRESS_REQUEST';
const NEW_ORDER_CHECKOUT_DELETE_ADDRESS_SUCCESS = 'NEW_ORDER_CHECKOUT_DELETE_ADDRESS_SUCCESS';
const NEW_ORDER_CHECKOUT_DELETE_ADDRESS_ERROR = 'NEW_ORDER_CHECKOUT_DELETE_ADDRESS_ERROR';

const NEW_ORDER_CHECKOUT_STEP_GET_LAST_CONTACT_REQUEST = 'NEW_ORDER_CHECKOUT_STEP_GET_LAST_CONTACT_REQUEST';
const NEW_ORDER_CHECKOUT_STEP_GET_LAST_CONTACT_SUCCESS = 'NEW_ORDER_CHECKOUT_STEP_GET_LAST_CONTACT_SUCCESS';
const NEW_ORDER_CHECKOUT_STEP_GET_LAST_CONTACT_ERROR = 'NEW_ORDER_CHECKOUT_STEP_GET_LAST_CONTACT_ERROR';

const NEW_ORDER_RESET_STATE = 'NEW_ORDER_RESET_STATE';

// Action Creators
// We separate post order in 2: post order image success y post order success
export function postOrder(requestOrder: PostOrderApiReq): NewOrderAction {
  return {
    type: NEW_ORDER_POST_ORDER_REQUEST,
    requestOrder,
  };
}

export function postOrderImageSuccess(imageIds: string[]): NewOrderAction {
  return {
    type: NEW_ORDER_POST_ORDER_IMAGE_SUCCESS,
    imageIds,
    // requestOrder,
  };
}

export function postOrderSuccess(createdOrder: Order): NewOrderAction {
  return {
    type: NEW_ORDER_POST_ORDER_SUCCESS,
    createdOrder,
  };
}

export function postOrderErrorFn(error: HTTPError): NewOrderAction {
  return {
    type: NEW_ORDER_POST_ORDER_ERROR,
    error,
  }
}

export function getLastContactInfo(country: string): NewOrderAction {
  return {
    type: NEW_ORDER_CHECKOUT_STEP_GET_LAST_CONTACT_REQUEST,
    country,
  };
}

export function getLastContactInfoSuccess(contactInfo: ContactInfo): NewOrderAction {
  return {
    type: NEW_ORDER_CHECKOUT_STEP_GET_LAST_CONTACT_SUCCESS,
    contactInfo,
  };
}

export function getLastContactInfoError(error: HTTPError): NewOrderAction {
  return {
    type: NEW_ORDER_CHECKOUT_STEP_GET_LAST_CONTACT_ERROR,
    error,
  };
}

export function fetchAddresses(): NewOrderAction {
  return {
    type: NEW_ORDER_CHECKOUT_FETCH_ADDRESSES_REQUEST,
  }
}

export function fetchAddressesSuccess(addresses: Address[]): NewOrderAction {
  return {
    type: NEW_ORDER_CHECKOUT_FETCH_ADDRESSES_SUCCESS,
    addresses,
  }
}

export function fetchAddressesErrorFn(error: HTTPError): NewOrderAction {
  return {
    type: NEW_ORDER_CHECKOUT_FETCH_ADDRESSES_ERROR,
    error,
  }
}

export function postAddress(address: AddressRequest): NewOrderAction {
  return {
    type: NEW_ORDER_CHECKOUT_POST_ADDRESS_REQUEST,
    address,
  }
}

export function postAddressSuccess(address: Address): NewOrderAction {
  return {
    type: NEW_ORDER_CHECKOUT_POST_ADDRESS_SUCCESS,
    address,
  }
}

export function postAddressErrorFn(error: HTTPError): NewOrderAction {
  return {
    type: NEW_ORDER_CHECKOUT_POST_ADDRESS_ERROR,
    error,
  }
}

export function editAddress(address: Address, addressId: string): NewOrderAction {
  return {
    type: NEW_ORDER_CHECKOUT_PUT_ADDRESS_REQUEST,
    address,
    addressId,
  }
}

export function editAddressSuccess(address: Address): NewOrderAction {
  return {
    type: NEW_ORDER_CHECKOUT_PUT_ADDRESS_SUCCESS,
    address,
  }
}

export function editAddressErrorFn(error: HTTPError): NewOrderAction {
  return {
    type: NEW_ORDER_CHECKOUT_PUT_ADDRESS_ERROR,
    error,
  }
}

export function deleteAddress(addressId: string): NewOrderAction {
  return {
    type: NEW_ORDER_CHECKOUT_DELETE_ADDRESS_REQUEST,
    addressId,
  }
}

export function deleteAddressSuccess(addressId: string): NewOrderAction {
  return {
    type: NEW_ORDER_CHECKOUT_DELETE_ADDRESS_SUCCESS,
    addressId,
  }
}

export function deleteAddressErrorFn(error: HTTPError): NewOrderAction {
  return {
    type: NEW_ORDER_CHECKOUT_DELETE_ADDRESS_ERROR,
    error,
  }
}

export function resetNewOrderState(): NewOrderAction {
  return {
    type: NEW_ORDER_RESET_STATE,
  };
}

export const actions = {
  NEW_ORDER_POST_ORDER_REQUEST,
  NEW_ORDER_POST_ORDER_IMAGE_SUCCESS,
  NEW_ORDER_POST_ORDER_SUCCESS,
  NEW_ORDER_POST_ORDER_ERROR,
  NEW_ORDER_CHECKOUT_FETCH_ADDRESSES_REQUEST,
  NEW_ORDER_CHECKOUT_FETCH_ADDRESSES_SUCCESS,
  NEW_ORDER_CHECKOUT_FETCH_ADDRESSES_ERROR,
  NEW_ORDER_CHECKOUT_POST_ADDRESS_REQUEST,
  NEW_ORDER_CHECKOUT_POST_ADDRESS_SUCCESS,
  NEW_ORDER_CHECKOUT_POST_ADDRESS_ERROR,
  NEW_ORDER_CHECKOUT_PUT_ADDRESS_REQUEST,
  NEW_ORDER_CHECKOUT_PUT_ADDRESS_SUCCESS,
  NEW_ORDER_CHECKOUT_PUT_ADDRESS_ERROR,
  NEW_ORDER_CHECKOUT_DELETE_ADDRESS_REQUEST,
  NEW_ORDER_CHECKOUT_DELETE_ADDRESS_SUCCESS,
  NEW_ORDER_CHECKOUT_DELETE_ADDRESS_ERROR,
  NEW_ORDER_RESET_STATE,
  NEW_ORDER_CHECKOUT_STEP_GET_LAST_CONTACT_REQUEST,
  NEW_ORDER_CHECKOUT_STEP_GET_LAST_CONTACT_SUCCESS,
  NEW_ORDER_CHECKOUT_STEP_GET_LAST_CONTACT_ERROR,
  postOrder,
  postOrderImageSuccess,
  postOrderSuccess,
  postOrderErrorFn,
  fetchAddresses,
  fetchAddressesSuccess,
  fetchAddressesErrorFn,
  postAddress,
  postAddressSuccess,
  postAddressErrorFn,
  editAddress,
  editAddressSuccess,
  editAddressErrorFn,
  deleteAddress,
  deleteAddressSuccess,
  deleteAddressErrorFn,
  getLastContactInfo,
  getLastContactInfoSuccess,
  getLastContactInfoError,
  resetNewOrderState,
};

export const initialState: NewOrderState = {
  searchStep: SearchStepInitialState,
  scrapStep: ScrapStepInitialState,
  productStep: ProductStepInitialState,
  quoteStep: QuoteStepInitialState,
  checkoutStep: CheckoutStepInitialState,
  checkoutSuccessStep: CheckoutSuccessStepInitialState,
};

// Reducer
export default function reducer(state: NewOrderState = initialState,
                                action: NewOrderAction): NewOrderState {
  switch (action.type) {
    case NEW_ORDER_POST_ORDER_REQUEST:
      return update(state, {
        isPosting: { $set: true },
        requestOrder: { $set: action.requestOrder },
        $unset: ['error'],
      });
    case NEW_ORDER_POST_ORDER_SUCCESS:
      return update(state, {
        $unset: ['isPosting', 'error', 'requestOrder'],
        createdOrder: { $set: action.createdOrder },
      });
    case NEW_ORDER_POST_ORDER_ERROR:
      return update(state, {
        $unset: ['isPosting', 'createdOrder', 'requestOrder'],
        postError: { $set: action.error },
      });
    case NEW_ORDER_CHECKOUT_FETCH_ADDRESSES_REQUEST:
      return update(state, {
        isFetchingAddresses: { $set: true },
        $unset: ['addresses'],
      });
    case NEW_ORDER_CHECKOUT_FETCH_ADDRESSES_SUCCESS:
      return update(state, {
        addresses: { $set: action.addresses },
        $unset: ['isFetchingAddresses'],
      });
    case NEW_ORDER_CHECKOUT_FETCH_ADDRESSES_ERROR:
      return update(state, {
        fetchAddressesError: { $set: action.error },
        $unset: ['isFetchingAddresses'],
      });
    case NEW_ORDER_CHECKOUT_POST_ADDRESS_REQUEST:
      return update(state, {
        isPostingAddress: { $set: true },
        $unset: ['closeAddressModal'],
      });
    case NEW_ORDER_CHECKOUT_POST_ADDRESS_SUCCESS:
      return update(state, {
        $unset: ['isPostingAddress'],
        addresses: { $push: [action.address] },
        closeAddressModal: { $set: true },
      });
    case NEW_ORDER_CHECKOUT_POST_ADDRESS_ERROR:
      return update(state, {
        $unset: ['isPostingAddress', 'closeAddressModal'],
        postAddressError: { $set: action.error },
      });
    case NEW_ORDER_CHECKOUT_PUT_ADDRESS_REQUEST:
      return update(state, {
        isPuttingAddress: { $set: true },
        $unset: ['closeAddressModal'],
      });
    case NEW_ORDER_CHECKOUT_PUT_ADDRESS_SUCCESS:
      if (!state.addresses) return state;
      const putIndex = state.addresses.findIndex((address: Address) =>
        action.address ? address.id === action.address.id : -1,
      );
      if (putIndex === -1) return state;
      return update(state, {
        $unset: ['isPuttingAddress'],
        addresses: { [putIndex]: { $merge: action.address } },
        closeAddressModal: { $set: true },
      });
    case NEW_ORDER_CHECKOUT_PUT_ADDRESS_ERROR:
      return update(state, {
        $unset: ['isPuttingAddress', 'closeAddressModal'],
        editAddressError: { $set: action.error },
      });
    case NEW_ORDER_CHECKOUT_DELETE_ADDRESS_REQUEST:
      return update(state, {
        $unset: ['deleteAddressErrorFn', 'successDelete'],
        isDeletingAddress: { $set: true },
      });
    case NEW_ORDER_CHECKOUT_DELETE_ADDRESS_SUCCESS:
      if (!state.addresses) return state;
      const deleteIndex = state.addresses.findIndex((address: Address) =>
        action.addressId === address.id);
      return update(state, {
        $unset: ['isDeletingAddress'],
        addresses: { $splice: [[deleteIndex, 1]] },
        successDelete: { $set: true },
      });
    case NEW_ORDER_CHECKOUT_DELETE_ADDRESS_ERROR:
      return update(state, {
        $unset: ['isDeletingAddress', 'successDelete'],
        deleteAddressError: { $set: action.error },
      });
    case NEW_ORDER_CHECKOUT_STEP_GET_LAST_CONTACT_SUCCESS:
      return update(state, {
        contactInfo: { $set: action.contactInfo },
      });
    case NEW_ORDER_CHECKOUT_STEP_GET_LAST_CONTACT_ERROR:
      return update(state, {
        getLastContactInfoError: { $set: action.error },
      });
    case SearchStepActions.SEARCH_STEP_SEARCH_REQUEST:
    case SearchStepActions.SEARCH_STEP_SEARCH_SUCCESS:
    case SearchStepActions.SEARCH_STEP_SEARCH_ERROR:
      return {
        ...state,
        searchStep: SearchStepReducer(state.searchStep, (action: any)),
      };
    case ProductStepActions.PRODUCT_STEP_FETCH_WISH_LISTS_REQUEST:
    case ProductStepActions.PRODUCT_STEP_FETCH_WISH_LISTS_SUCCESS:
    case ProductStepActions.PRODUCT_STEP_FETCH_WISH_LISTS_ERROR:
    case ProductStepActions.PRODUCT_STEP_POST_PRODUCT_WISH_LIST_REQUEST:
    case ProductStepActions.PRODUCT_STEP_POST_PRODUCT_WISH_LIST_IMAGE_SUCCESS:
    case ProductStepActions.PRODUCT_STEP_POST_PRODUCT_WISH_LIST_SUCCESS:
    case ProductStepActions.PRODUCT_STEP_POST_PRODUCT_WISH_LIST_ERROR:
    case ProductStepActions.PRODUCT_STEP_POST_NEW_WISH_LIST_REQUEST:
    case ProductStepActions.PRODUCT_STEP_POST_NEW_WISH_LIST_SUCCESS:
    case ProductStepActions.PRODUCT_STEP_POST_NEW_WISH_LIST_ERROR:
    case ProductStepActions.PRODUCT_STEP_FORECAST_PRICES_REQUEST:
    case ProductStepActions.PRODUCT_STEP_FORECAST_PRICES_SUCCESS:
    case ProductStepActions.PRODUCT_STEP_FORECAST_PRICES_ERROR:
      return {
        ...state,
        productStep: ProductStepReducer(state.productStep, (action: any)),
      };
    case QuoteStepActions.QUOTE_STEP_APPLY_COUPON_REQUEST:
    case QuoteStepActions.QUOTE_STEP_REMOVE_COUPON_REQUEST:
    case QuoteStepActions.QUOTE_STEP_FORECAST_PRICES_REQUEST:
    case QuoteStepActions.QUOTE_STEP_FORECAST_PRICES_SUCCESS:
    case QuoteStepActions.QUOTE_STEP_FORECAST_PRICES_ERROR:
    case QuoteStepActions.QUOTE_STEP_MISSING_SUM_CART_REQUEST:
    case QuoteStepActions.QUOTE_STEP_MISSING_SUM_ITEMS_REQUEST:
    case QuoteStepActions.QUOTE_STEP_MISSING_SUM_SUCCESS:
    case QuoteStepActions.QUOTE_STEP_MISSING_SUM_ERROR:
    case QuoteStepActions.QUOTE_STEP_FETCH_RELATED_PRODUCTS_REQUEST:
    case QuoteStepActions.QUOTE_STEP_FETCH_RELATED_PRODUCTS_SUCCESS:
    case QuoteStepActions.QUOTE_STEP_FETCH_RELATED_PRODUCTS_ERROR:
    case QuoteStepActions.QUOTE_STEP_RESET_STATE:
      return {
        ...state,
        quoteStep: QuoteStepReducer(state.quoteStep, (action: any)),
      };
    case CheckoutStepActions.CHECKOUT_STEP_FETCH_ADDRESSES_REQUEST:
    case CheckoutStepActions.CHECKOUT_STEP_FETCH_ADDRESSES_SUCCESS:
    case CheckoutStepActions.CHECKOUT_STEP_FETCH_ADDRESSES_ERROR:
    case CheckoutStepActions.CHECKOUT_STEP_FETCH_PAYMENT_METHOD_REQUEST:
    case CheckoutStepActions.CHECKOUT_STEP_FETCH_PAYMENT_METHOD_SUCCESS:
    case CheckoutStepActions.CHECKOUT_STEP_FETCH_PAYMENT_METHOD_ERROR:
    case CheckoutStepActions.CHECKOUT_STEP_APPLY_COUPON_REQUEST:
    case CheckoutStepActions.CHECKOUT_STEP_APPLY_COUPON_SUCCESS:
    case CheckoutStepActions.CHECKOUT_STEP_APPLY_COUPON_ERROR:
    case CheckoutStepActions.CHECKOUT_STEP_REMOVE_COUPON_REQUEST:
    case CheckoutStepActions.CHECKOUT_STEP_REMOVE_COUPON_SUCCESS:
    case CheckoutStepActions.CHECKOUT_STEP_REMOVE_COUPON_ERROR:
    case CheckoutStepActions.CHECKOUT_STEP_FETCH_ORDER_REQUEST:
    case CheckoutStepActions.CHECKOUT_STEP_FETCH_ORDER_SUCCESS:
    case CheckoutStepActions.CHECKOUT_STEP_FETCH_ORDER_ERROR:
    case CheckoutStepActions.CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_REQUEST:
    case CheckoutStepActions.CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_SUCCESS:
    case CheckoutStepActions.CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_ERROR:
    case CheckoutStepActions.CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_REQUEST:
    case CheckoutStepActions.CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_SUCCESS:
    case CheckoutStepActions.CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_ERROR:
    case CheckoutStepActions.CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_REQUEST:
    case CheckoutStepActions.CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_SUCCESS:
    case CheckoutStepActions.CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_ERROR:
    case CheckoutStepActions.CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_REQUEST:
    case CheckoutStepActions.CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_SUCCESS:
    case CheckoutStepActions.CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_ERROR:
    case CheckoutStepActions.CHECKOUT_STEP_FETCH_CHECKOUT_DATA_SUCCESS:
    case CheckoutStepActions.CHECKOUT_STEP_FETCH_APPLIED_COUPON_SUCCESS:
    case CheckoutStepActions.CHECKOUT_STEP_LOADING_EXECUTE:
    case CheckoutStepActions.CHECKOUT_STEP_POST_PAYMENT_EXECUTE_REQUEST:
    case CheckoutStepActions.CHECKOUT_STEP_POST_PAYMENT_EXECUTE_SUCCESS:
    case CheckoutStepActions.CHECKOUT_STEP_POST_PAYMENT_EXECUTE_ERROR:
    case CheckoutStepActions.CHECKOUT_STEP_POST_PAYMENT_EXECUTE_UI_ERROR:
    case CheckoutStepActions.CHECKOUT_STEP_NOT_FOUND_LIBRARY_ERROR:
    case CheckoutStepActions.CHECKOUT_STEP_RESET_STATE:
      return {
        ...state,
        checkoutStep: CheckoutStepReducer(state.checkoutStep, (action: any)),
      };
    case CheckoutSuccessStepActions.CHECKOUT_SUCCESS_STEP_RESET_STATE:
    case CheckoutSuccessStepActions.CHECKOUT_SUCCESS_STEP_FILL_ORDER:
    case CheckoutSuccessStepActions.CHECKOUT_SUCCESS_FETCH_ORDER_REQUEST:
    case CheckoutSuccessStepActions.CHECKOUT_SUCCESS_FETCH_ORDER_SUCCESS:
      return {
        ...state,
        checkoutSuccessStep: CheckoutSuccessStepReducer(state.checkoutSuccessStep, (action: any)),
      };
    case NEW_ORDER_RESET_STATE:
      return initialState;
    default:
      return state;
  }
}

// Epics
export function postOrderImageEpic(action$: Observable<NewOrderAction>): Observable<NewOrderAction> {
  return action$.pipe(
    ofType(NEW_ORDER_POST_ORDER_REQUEST),
    mergeMap(({ requestOrder }) => {
      const obs = requestOrder.items.map((item: PostOrderItemApiReq) => {
        if (item.image) {
          return uploadImageURLApi({
            url: item.image,
            resourceGroup: 'PRODUCT',
          });
        } else if (item.imageURI) {
          const fd = new FormData();
          fd.append('file', dataURItoBlob(item.imageURI));
          fd.append('resourceGroup', 'PRODUCT');
          return uploadImageApi(fd, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        }
      });
      return forkJoin(obs).pipe(
        mergeMap((imagesResponse: { id: string }[]) =>
          of(postOrderImageSuccess(imagesResponse.map(({ id }) => id))),
        ),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postOrderErrorFn(e),
              setNewAlert('new-order', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(postOrderErrorFn(e),
              setNewAlert('new-order', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(postOrderErrorFn(e));
        }));
    }));
}

export function postOrderEpic(action$: Observable<NewOrderAction>, state$: StateObservable<RootState>): Observable<NewOrderAction> {
  return action$.pipe(
    ofType(NEW_ORDER_POST_ORDER_IMAGE_SUCCESS),
    mergeMap(({ imageIds }) => {
      const state: RootState = state$.value;
      const requestOrder = { ...state.newOrder.requestOrder };
      imageIds.forEach((imageId: string, n: number) => {
        if (requestOrder && requestOrder.items) {
          requestOrder.items[n].image = imageId;
          delete requestOrder.items[n].imageURI;
        }
      });
      // TODO: add userReferrer and landingUrl
      const userReferrer = window.localStorage.getItem('userReferrer');
      const landingUrl = window.localStorage.getItem('landingUrl');
      if (userReferrer) {
        requestOrder.userReferrer = userReferrer;
      }
      if (landingUrl) {
        requestOrder.landingUrl = landingUrl;
      }
      return postOrderApi(requestOrder).pipe(
        map((createdOrder: Order) => postOrderSuccess(createdOrder)),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postOrderErrorFn(e),
              setNewAlert('new-order', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(postOrderErrorFn(e),
              setNewAlert('new-order', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(postOrderErrorFn(e));
        }));
    }));
}

export function redirectNewOrderToCheckoutAfterSuccess(action$: Observable<NewOrderAction>): Observable<NewOrderAction> {
  return action$.pipe(
    ofType(NEW_ORDER_POST_ORDER_SUCCESS),
    mergeMap(({ createdOrder }) => of(push(`/checkout/order/${createdOrder.id}`))),
  );
}

export function postNewOrderCheckoutAddressEpic(action$: Observable<NewOrderAction>): Observable<NewOrderAction> {
  return action$.pipe(
    ofType(NEW_ORDER_CHECKOUT_POST_ADDRESS_REQUEST),
    mergeMap(({ address }) => postAddressApi(address).pipe(
      map((address: Address) => postAddressSuccess(address)),
      catchError((e: HTTPError) => of(postAddressErrorFn(e)))),
    ));
}

export function editNewOrderCheckoutAddressEpic(action$: Observable<NewOrderAction>): Observable<NewOrderAction> {
  return action$.pipe(
    ofType(NEW_ORDER_CHECKOUT_PUT_ADDRESS_REQUEST),
    mergeMap(({ address, addressId }) => {
      return putAddressApi(address, {
        route: addressEndpoints.PUT_ADDRESS.replace(':id', addressId),
      }).pipe(
        map((response: Address) => editAddressSuccess(response)),
        catchError((e: HTTPError) => of(editAddressErrorFn(e))))
    }));
}

export function deleteNewOrderCheckoutAddressEpic(action$: Observable<NewOrderAction>): Observable<NewOrderAction> {
  return action$.pipe(
    ofType(NEW_ORDER_CHECKOUT_DELETE_ADDRESS_REQUEST),
    mergeMap(({ addressId }) => deleteAddressApi(addressId, {
        route: addressEndpoints.DELETE_ADDRESS.replace(':id', addressId),
      }).pipe(
      map(() => deleteAddressSuccess(addressId)),
      catchError((e: HTTPError) => of(deleteAddressErrorFn(e)))),
    ));
}



// WEBPACK FOOTER //
// ./src/modules/new-order/duck.js